import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initialSidebarNavigation, resolveSidebarNavigation } from './sidebar-navigation-state.js';
import { createSidebarSessionReader } from './sidebar-session-reader.js';
import { resolveProjectRecordId } from './projectProjection.js';
import { initialConversationDraft, resolveConversationDraft } from './conversation-draft-state.js';
const COLLECTION_WINDOW_PAGE_SIZE = 20;

const sessionIdFor = item => String(item?.session_id ?? item?.sessionId ?? item?.id ?? '');
const sessionProjectFor = item => String(item?.project_id ?? item?.projectId ?? '');
export const resolveSidebarSessionTarget = (projects, projectId, items, sessionId) => {
  const project = projects.find(item => resolveProjectRecordId(item) === projectId && item.status !== 'archived');
  const session = items.find(item => sessionIdFor(item) === sessionId && sessionProjectFor(item) === projectId);
  return project && session ? { projectId, sessionId } : null;
};
export const sidebarSelectionReady = (navigation, scopeKey, projectId, projection, startedId) => (
  navigation?.scopeKey === scopeKey && navigation?.status === 'pending'
  && navigation.projectId === projectId && projection.scope.projectId === projectId
  && projection.bootStatus === 'ready' && startedId !== navigation.requestId
);
const matchesProject = (projects, projectId) => projects.some(item => (
  resolveProjectRecordId(item) === projectId && item.status !== 'archived'
));
const selectionIdle = (navigation, scopeKey) => (
  navigation?.status !== 'pending' || navigation.scopeKey !== scopeKey
);
const selectionCancelled = (navigation, activeProjectId) => (
  ![navigation.originProjectId, navigation.projectId].includes(activeProjectId)
);
const selectionFailed = (navigation, projection) => (
  projection.scope.projectId === navigation.projectId && projection.bootStatus === 'failed'
);
const draftResetPending = (navigation, projection) => (
  navigation.intent === 'create_session' && Boolean(projection.scope.activeSessionId)
);

const activateSidebarProject = (current, project, sessionId) => {
  try {
    if (!sessionId || resolveProjectRecordId(project) !== current.activeProjectId) current.projects.onProjectSelect(project);
    return null;
  } catch (error) { return error; }
};

export const resolveSidebarSelectionPhase = (navigation, scopeKey, activeProjectId, projection, startedId) => {
  if (selectionIdle(navigation, scopeKey)) return 'idle';
  if (selectionCancelled(navigation, activeProjectId)) return 'cancelled';
  if (selectionFailed(navigation, projection)) return 'failed';
  if (draftResetPending(navigation, projection)) return 'waiting';
  return sidebarSelectionReady(navigation, scopeKey, activeProjectId, projection, startedId) ? 'ready' : 'waiting';
};

/** Waiters follow accepted selection completion, never dispatch acknowledgement. */
export const createSidebarSelectionCompletion = () => {
  let pending = null;
  const cancel = () => { pending?.resolve(false); pending = null; };
  return {
    cancel,
    find: key => pending?.key === key ? pending : null,
    begin: (key, requestId) => {
      if (pending?.key === key) return pending;
      cancel();
      let resolve;
      const promise = new Promise(accept => { resolve = accept; });
      pending = { key, requestId, promise, resolve };
      return pending;
    },
    finish: (requestId, status) => {
      if (pending?.requestId !== requestId) return false;
      pending.resolve(status === 'completed'); pending = null;
      return true;
    },
  };
};

const completeProjectNavigation = ({ commitDraft, current, project, request, setNavigation }) => {
  const next = { ...request, status: 'completed', error: null };
  setNavigation(next);
  const error = activateSidebarProject(current, project, request.sessionId);
  if (error) {
    setNavigation({ ...next, status: 'failed', error });
    return false;
  }
  commitDraft({ type: 'clear' });
  return true;
};

const beginProjectSelection = ({ completion, current, key, project, request, setNavigation }) => {
  const waiting = completion.begin(key, request.requestId);
  const next = { ...request, status: 'pending', error: null };
  setNavigation(next);
  const error = activateSidebarProject(current, project, request.sessionId);
  if (error) {
    setNavigation({ ...next, status: 'failed', error });
    completion.finish(request.requestId, 'failed');
  }
  return waiting.promise;
};

const activeSessionIdFor = projects => projects.projection?.scope?.activeSessionId ?? '';
const expansionModeFor = options => options.sidebarProjectExpansion === 'multiple' ? 'multiple' : 'single';
const expandedIdsForScope = (view, scopeKey) => (
  view.scopeKey === scopeKey ? view.state.expandedIds : []
);
const navigationForScope = (navigation, scopeKey) => (
  navigation?.scopeKey === scopeKey ? navigation : null
);
const suppressesSessionRestore = (conversationDraft, navigation, activeProjectId, scopeKey) => (
  (conversationDraft.status === 'active' && conversationDraft.projectId === activeProjectId)
  || (navigation?.scopeKey === scopeKey && navigation.projectId === activeProjectId)
);
const callSidebarReader = (readerRef, method, ...args) => (
  readerRef.current?.[method]?.(...args) ?? Promise.resolve()
);

/** Sidebar reads never bind or reset the conversation Controller. Explicit navigation may do so. */
export const useProjectSidebarNavigation = ({ api, options, projects, workspaceLocation, onSelectSession, actions }) => {
  const scopeKey = workspaceLocation.scopeKey;
  const activeProjectId = workspaceLocation.effectiveProjectId;
  const activeSessionId = activeSessionIdFor(projects);
  const canonicalSessionProjection = projects.projection?.sessions;
  const expansionMode = expansionModeFor(options);
  const [view, setView] = useState(() => ({ scopeKey, state: initialSidebarNavigation() }));
  const [revision, setRevision] = useState(0);
  const [navigation, setNavigation] = useState(null);
  const [conversationDraft, setConversationDraft] = useState(initialConversationDraft);
  const readerRef = useRef(null);
  const completion = useMemo(createSidebarSelectionCompletion, []);
  const currentRef = useRef({ projects, onSelectSession, activeProjectId, scopeKey, actions });
  currentRef.current = { projects, onSelectSession, activeProjectId, scopeKey, actions };
  const stateRef = useRef(view);
  stateRef.current = view;
  const requestSequence = useRef(0);
  const startedRequest = useRef(0);
  const expandedRef = useRef([]);
  const draftScopeRef = useRef(scopeKey);
  const draftRef = useRef(conversationDraft);
  draftRef.current = conversationDraft;
  const commitDraft = useCallback((event) => {
    const next = resolveConversationDraft(draftRef.current, event);
    draftRef.current = next;
    setConversationDraft(next);
    return next;
  }, []);
  const scope = useMemo(() => ({
    key: JSON.stringify([options.userId, options.instanceId, options.domainPackDomain, options.domainPackVersion]),
    instanceId: options.instanceId, userId: options.userId,
    domainPackDomain: options.domainPackDomain, domainPackVersion: options.domainPackVersion,
  }), [options.instanceId, options.userId, options.domainPackDomain, options.domainPackVersion]);
  useEffect(() => {
    if (draftScopeRef.current === scopeKey) return;
    draftScopeRef.current = scopeKey;
    commitDraft({ type: 'clear' });
  }, [commitDraft, scopeKey]);
  useEffect(() => {
    const reader = createSidebarSessionReader({
      sessionAPI: api.sessionAPI, scope, projects: currentRef.current.projects.projectItems,
      pageSize: COLLECTION_WINDOW_PAGE_SIZE,
      onChange: () => setRevision(value => value + 1),
      canonicalActions: { refresh: () => currentRef.current.actions.refreshSessionList(),
        loadMore: () => currentRef.current.actions.loadMoreSessions() },
      currentProjection: projectId => {
        const projection = currentRef.current.projects.projection;
        return projection.scope.projectId === projectId ? projection.sessions : null;
      },
    });
    readerRef.current = reader;
    expandedRef.current = [];
    setRevision(value => value + 1);
    return () => { reader.dispose(); readerRef.current = null; requestSequence.current += 1; completion.cancel(); };
  }, [api.sessionAPI, completion, scope]);
  useEffect(() => {
    const reader = readerRef.current;
    reader?.updateProjects(projects.projectItems);
    reader?.bindCanonicalProjection(activeProjectId, canonicalSessionProjection);
    const expandedIds = view.scopeKey === scopeKey ? view.state.expandedIds : [];
    expandedRef.current.filter(id => !expandedIds.includes(id)).forEach(id => reader?.close(id));
    expandedIds.filter(id => matchesProject(projects.projectItems, id))
      .forEach(id => { void reader?.open(id).catch(() => {}); });
    expandedRef.current = expandedIds;
  }, [activeProjectId, api.sessionAPI, canonicalSessionProjection, projects.projectItems, scope, scopeKey, view]);
  const seededProject = useRef('');
  useEffect(() => {
    const key = `${scopeKey}:${activeProjectId}`;
    if (seededProject.current === key || !matchesProject(projects.projectItems, activeProjectId)) return;
    seededProject.current = key;
    const state = stateRef.current.scopeKey === scopeKey ? stateRef.current.state : initialSidebarNavigation();
    setView({ scopeKey, state: resolveSidebarNavigation(state, { type: 'expand', projectId: activeProjectId }, expansionMode).state });
  }, [activeProjectId, expansionMode, projects.projectItems, scopeKey]);
  const reset = useCallback(() => {
    // Pointer/keyboard browsing resets only the sidebar shortcut, not an accepted navigation request.
    const current = stateRef.current;
    if (!current.state.armedId) return;
    const next = { scopeKey, state: resolveSidebarNavigation(current.state, { type: 'reset' }).state };
    stateRef.current = next; setView(next);
  }, [scopeKey]);
  const navigate = useCallback((projectId, sessionId = '', intent = 'open_project') => {
    const current = currentRef.current;
    const project = current.projects.projectItems.find(item => resolveProjectRecordId(item) === projectId);
    if (!matchesProject(current.projects.projectItems, projectId)) return Promise.resolve(false);
    const key = JSON.stringify([scopeKey, projectId, sessionId, intent]);
    const pending = completion.find(key);
    if (pending) return pending.promise;
    const requestId = ++requestSequence.current;
    completion.cancel();
    const request = { scopeKey, projectId, sessionId, intent, requestId, originProjectId: current.activeProjectId };
    if (!sessionId && intent !== 'create_session') {
      return Promise.resolve(completeProjectNavigation({ commitDraft, current, project, request, setNavigation }));
    }
    return beginProjectSelection({ completion, current, key, project, request, setNavigation });
  }, [commitDraft, completion, scopeKey]);
  const dispatch = useCallback((type, project) => {
    const projectId = resolveProjectRecordId(project);
    if (!matchesProject(currentRef.current.projects.projectItems, projectId)) return false;
    const current = stateRef.current.scopeKey === scopeKey ? stateRef.current.state : initialSidebarNavigation();
    const decision = resolveSidebarNavigation(current, { type, projectId }, expansionMode);
    const next = { scopeKey, state: decision.state };
    stateRef.current = next; setView(next);
    if (decision.action === 'open_project' && type === 'open') navigate(projectId);
    return decision.action === 'open_project';
  }, [expansionMode, navigate, scopeKey]);
  const openProject = useCallback(project => dispatch('open', project), [dispatch]);
  const openSession = useCallback((projectId, sessionId) => {
    const target = resolveSidebarSessionTarget(currentRef.current.projects.projectItems, projectId,
      readerRef.current?.getProjection(projectId).items ?? [], sessionId);
    if (!target) return Promise.resolve(false);
    reset(); return navigate(target.projectId, target.sessionId);
  }, [navigate, reset]);
  const createSession = useCallback(project => {
    const projectId = resolveProjectRecordId(project);
    if (draftRef.current.status === 'active' && draftRef.current.projectId === projectId) {
      return Promise.resolve(true);
    }
    return navigate(projectId, '', 'create_session');
  }, [navigate]);
  useEffect(() => {
    const phase = resolveSidebarSelectionPhase(navigation, scopeKey, activeProjectId, projects.projection, startedRequest.current);
    if (phase === 'idle') return;
    const settle = (status, error = null) => {
      if (requestSequence.current !== navigation.requestId) return;
      setNavigation({ ...navigation, status, error });
      completion.finish(navigation.requestId, status);
    };
    if (phase === 'cancelled') {
      requestSequence.current += 1; completion.cancel();
      setNavigation({ ...navigation, status: 'cancelled' });
      return;
    }
    if (phase === 'failed') {
      settle('failed', projects.projection.recovery?.error ?? new Error('Project conversation admission failed'));
      return;
    }
    if (phase !== 'ready') return;
    startedRequest.current = navigation.requestId;
    if (navigation.intent === 'create_session') {
      commitDraft({ type: 'activate', projectId: navigation.projectId });
      settle('completed');
      return;
    }
    void Promise.resolve().then(() => {
      if (requestSequence.current !== navigation.requestId) return;
      return onSelectSession(navigation.sessionId);
    }).then(() => {
      if (requestSequence.current !== navigation.requestId) return;
      commitDraft({ type: 'clear' }); settle('completed');
    }, error => settle('failed', error));
  }, [activeProjectId, commitDraft, completion, navigation, onSelectSession, projects.projection, scopeKey]);
  useEffect(() => {
    if (conversationDraft.status !== 'active') return;
    if (activeSessionId || conversationDraft.projectId !== activeProjectId) commitDraft({ type: 'clear' });
  }, [activeProjectId, activeSessionId, commitDraft, conversationDraft]);
  const expandedIds = expandedIdsForScope(view, scopeKey);
  const projection = Object.fromEntries(expandedIds.map(id => [id, readerRef.current?.getProjection(id) ?? { items: [], loading: true, canRenderSessions: true, window: {}, error: null }]));
  return {
    revision, expandedIds, projection, reset, dispatch, openProject, openSession, createSession, conversationDraft,
    navigation: navigationForScope(navigation, scopeKey),
    suppressSessionRestore: suppressesSessionRestore(conversationDraft, navigation, activeProjectId, scopeKey),
    invalidate: () => callSidebarReader(readerRef, 'invalidate'),
    refreshIfStale: () => callSidebarReader(readerRef, 'refreshIfStale'),
    refresh: projectId => callSidebarReader(readerRef, 'refresh', projectId),
    loadMore: projectId => callSidebarReader(readerRef, 'loadMore', projectId),
  };
};
